/**
 * ERPLayout Component
 * 
 * Layout principal do sistema ERP.
 * Fornece estrutura base com Sidebar de navegação, Header e área de conteúdo.
 * 
 * Funcionalidades:
 * - Sidebar com navegação por módulos
 * - Header com informações do usuário e notificações
 * - Área de conteúdo responsiva
 * - Menu colapsável
 */

import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions, MODULES, ACTIONS } from '../../hooks/usePermissions'
import { useIsMobileTablet } from '../../hooks/useIsMobileTablet'
import Sidebar from './components/Sidebar'
import Header from './components/Header'
import MobileBottomNav from '../../components/MobileBottomNav/MobileBottomNav'
import FloatingChat from '../../components/FloatingChat/FloatingChat'
import SellerAssistantPanel from '../../components/SellerAssistant/SellerAssistantPanel'
import OperationalAssistantPanel from '../../components/OperationalAssistant/OperationalAssistantPanel'
import InstallAppBanner from '../../components/PwaInstall/InstallAppBanner'
import './ERPLayout.css'

function ERPLayout({ children }) {
  const { isAuthenticated, user, canAccessERP, loading } = useAuth()
  const { hasPermission, loading: permissionsLoading } = usePermissions()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobileTablet = useIsMobileTablet()
  const canAccessMessages = !permissionsLoading && hasPermission(MODULES.MESSAGES, ACTIONS.VIEW)
  const canAccessSellerAssistant = !permissionsLoading && hasPermission(MODULES.SALES, ACTIONS.SALES_SELLER_ASSISTANT)
  const canAccessOperationalAssistant = !permissionsLoading && hasPermission(MODULES.ERP, ACTIONS.ERP_OPERATIONAL_ASSISTANT)
  const [assistantOpen, setAssistantOpen] = useState(() => {
    try {
      return sessionStorage.getItem('seller-assistant-open') === 'true'
    } catch {
      return false
    }
  })
  const [operationalAssistantOpen, setOperationalAssistantOpen] = useState(() => {
    try {
      return sessionStorage.getItem('operational-assistant-open') === 'true'
    } catch {
      return false
    }
  })
  
  // Verificar se está na página de verificação de espelho (sem header)
  const isMirrorVerificationPage = location.pathname.includes('/purchases/mirrors/verify/')
  const isTvDisplayPage = location.pathname.includes('/erp/vendas/tv-display')
  /** Contagem/gestão de auditoria: sem menu inferior (mais espaço + FAB da câmera). */
  const isStockAuditDetailPage = /^\/erp\/stock\/audits\/[^/]+/.test(location.pathname)
  const hideMobileBottomNav = isTvDisplayPage || isStockAuditDetailPage
  
  // Sidebar sempre começa colapsado
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)

  useEffect(() => {
    try {
      sessionStorage.setItem('seller-assistant-open', String(assistantOpen))
    } catch {
      /* ignore */
    }
  }, [assistantOpen])

  useEffect(() => {
    try {
      sessionStorage.setItem('operational-assistant-open', String(operationalAssistantOpen))
    } catch {
      /* ignore */
    }
  }, [operationalAssistantOpen])

  useEffect(() => {
    // Aguardar carregamento do usuário
    if (loading) return

    // Redirecionar para login se não estiver autenticado
    if (!isAuthenticated) {
      navigate('/login?redirect=/erp')
      return
    }

    // Verificar se usuário pode acessar ERP
    if (!canAccessERP()) {
      // Usuário sem grupo de acesso não pode acessar ERP
      navigate('/?erp_access_denied=true')
      return
    }
  }, [isAuthenticated, user, canAccessERP, loading, navigate])

  // Não renderizar enquanto verifica acesso
  if (loading || !isAuthenticated || !canAccessERP()) {
    return null
  }

  const isSidebarExpanded = !sidebarCollapsed

  return (
    <div className={`erp-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${isMobileTablet ? 'mobile-tablet' : ''} ${isTvDisplayPage ? 'tv-display-mode' : ''} ${hideMobileBottomNav ? 'hide-mobile-bottom-nav' : ''}`}>
      {/* Sidebar apenas em desktop */}
      {!isMobileTablet && !isTvDisplayPage && (
        <Sidebar 
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      )}
      
      <div className="erp-main">
        {!isMirrorVerificationPage && !isTvDisplayPage && (
          <Header 
            sidebarCollapsed={sidebarCollapsed}
            isSidebarExpanded={isSidebarExpanded}
            assistantOpen={assistantOpen}
            onToggleAssistant={() => setAssistantOpen((open) => !open)}
            operationalAssistantOpen={operationalAssistantOpen}
            onToggleOperationalAssistant={() => setOperationalAssistantOpen((open) => !open)}
          />
        )}
        
        <main className={`erp-content ${isMirrorVerificationPage || isTvDisplayPage ? 'no-header tv-display-content' : ''}`}>
          {!isMirrorVerificationPage && !isTvDisplayPage && <InstallAppBanner />}
          {children}
        </main>
      </div>

      {/* MobileBottomNav apenas em mobile/tablet (oculto em TV e auditoria de estoque) */}
      {isMobileTablet && !hideMobileBottomNav && <MobileBottomNav />}
      
      {/* FloatingChat apenas em desktop (mobile usa MobileBottomNav → /erp/mensagens) */}
      {!isMobileTablet && canAccessMessages && !isTvDisplayPage && <FloatingChat />}

      {/* Assistente de vendas — janela flutuante (permanece ativa ao navegar no ERP) */}
      {!isMobileTablet && canAccessSellerAssistant && !isTvDisplayPage && (
        <SellerAssistantPanel
          open={assistantOpen}
          onClose={() => setAssistantOpen(false)}
        />
      )}

      {!isMobileTablet && canAccessOperationalAssistant && !isTvDisplayPage && (
        <OperationalAssistantPanel
          open={operationalAssistantOpen}
          onClose={() => setOperationalAssistantOpen(false)}
        />
      )}
    </div>
  )
}

export default ERPLayout

